import React, { useState, useEffect, useCallback, useRef } from 'react';
import { buildReceiptData, HistoryItem, ReceiptModal } from './HistoryShared';
import AdminBankRequests from './AdminBankRequests';
import AdminMarketRequests from './AdminMarketRequests';
import PlayerAvatar from './PlayerAvatar';
import { copyToClipboard as copyToClipboardUtil } from '../utils/clipboard';

const COLORS = ['#1d1d21', '#f9fffe', '#3c44aa', '#835432', '#3ab3da', '#169c9c', '#5e7c16', '#8932b8', '#fed83d', '#b02e26', '#c74ebd', '#474f52', '#f9801d', '#9d9d97', '#80c71f', '#f38baa'];

function Admin({ tgId, adminNickname, isAdmin, isModerator, canManageBank, canManageWarehouse, initialFocus, onFocusConsumed, requestFocus, onRequestFocusConsumed }) {
  const ROLE_LABELS = { banker: 'Банкир', warehouse: 'Работник склада', seller: 'Продавец', moderator: 'Модератор' };
  const ASSIGNABLE_ROLES = ['banker', 'warehouse', 'seller', 'moderator'];
  const canIssueInvoice = isAdmin || isModerator;
  const [tab, setTab] = useState('general'); // general, bank, market
  const [subTab, setSubTab] = useState('main'); // main, players, system
  const [bankSubTab, setBankSubTab] = useState('accounts'); // accounts, withdrawals, deposits, decryptor
  const [marketSubTab, setMarketSubTab] = useState('overview'); // overview, requests, ads
  const [adStats, setAdStats] = useState({ active_ads: 0, revenue_today: 0 });
  const [adPrice, setAdPrice] = useState(1);
  const [adCardNumber, setAdCardNumber] = useState('');
  // Счёт казны: виден только админам, принимает остатки удалённых счетов и
  // по умолчанию — оплату за рекламу, продвижение и дизайн карты.
  const [treasuryAccount, setTreasuryAccount] = useState('');
  const [savingTreasury, setSavingTreasury] = useState(false);
  const [promotionPricePerDay, setPromotionPricePerDay] = useState(5);
  const [promotionLabelPrice, setPromotionLabelPrice] = useState(15);
  const [cardDesignPrice, setCardDesignPrice] = useState(16);
  const [adminAds, setAdminAds] = useState([]);
  const [editAdModal, setEditAdModal] = useState(null);
  const [deleteAdModal, setDeleteAdModal] = useState(null);

  const [marketItems, setMarketItems] = useState([]);
  const [editItemModal, setEditItemModal] = useState(null);
  const [deleteItemModal, setDeleteItemModal] = useState(null);
  const [deleteItemReason, setDeleteItemReason] = useState('');
  const [deleteItemError, setDeleteItemError] = useState('');
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  const [players, setPlayers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [notificationChatId, setNotificationChatId] = useState('');
  const [warehouseChatId, setWarehouseChatId] = useState('');
  const [backupChatId, setBackupChatId] = useState('');
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [allowUnlinkedLogin, setAllowUnlinkedLogin] = useState(false);
  const [announceText, setAnnounceText] = useState('');
  const [announceMedia, setAnnounceMedia] = useState([]); // [{ kind, data_base64, name }]
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // { url } — модалка вставки ссылки
  const announceTextRef = useRef(null);
  const announceFileRef = useRef(null);
  
  const [search, setSearch] = useState('');
  const [filterModal, setFilterModal] = useState(false);
  const [filters, setFilters] = useState({ account: 'ALL', balance: 'ALL', status: 'ALL' });
  const [tempFilters, setTempFilters] = useState({ account: 'ALL', balance: 'ALL', status: 'ALL' });
  
  const [expandedIds, setExpandedIds] = useState([]);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  
  // Состояния для истории счета
  const [adminHistoryView, setAdminHistoryView] = useState({ show: false, tgId: null, accountNum: null, accountId: null });
  const [adminHistory, setAdminHistory] = useState([]);
  const [adminHistoryError, setAdminHistoryError] = useState('');
  const [adminHistoryPage, setAdminHistoryPage] = useState(1);
  const [adminFilterType, setAdminFilterType] = useState('ALL');
  const [adminFilterAccount, setAdminFilterAccount] = useState('ALL');
  const [highlightOps, setHighlightOps] = useState([]);
  const highlightItemRef = useRef(null);
  // Фокус на конкретную заявку (приход из «Профиля действий» по клику на #ID) —
  // прокидывается в дочерние разделы заявок (банк/маркет) для прокрутки и подсветки.
  const [reqFocus, setReqFocus] = useState(null); // { scope: 'bank'|'market', requestId, nonce }

  // Состояния для настроек счета
  const [accSettingsModal, setAccSettingsModal] = useState(false);
  const [accEditData, setAccEditData] = useState({ tgId: '', originalNumber: '', number: '', name: '', balance: 0, color: '', image: '' });
  const [accDesignRemoved, setAccDesignRemoved] = useState(false);
  
  // Состояния для действий со счетом
  const [accActionModal, setAccActionModal] = useState({ show: false, action: '', tgId: null, accountNum: null });
  const [accActionReason, setAccActionReason] = useState('');
  const [accActionError, setAccActionError] = useState('');

  // Состояния для бана
  const [banModal, setBanModal] = useState(false);
  const [banTarget, setBanTarget] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banError, setBanError] = useState('');

  // Состояния для штрафов
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [invoiceDestAccount, setInvoiceDestAccount] = useState('');
  const [invoiceModalDestFocus, setInvoiceModalDestFocus] = useState(false);
  const [invoiceNickFocus, setInvoiceNickFocus] = useState(false);
  const [invoiceDestFocus, setInvoiceDestFocus] = useState(false);
  const [invoiceNick, setInvoiceNick] = useState('');
  const [invoiceNickAmount, setInvoiceNickAmount] = useState('');
  const [invoiceNickDest, setInvoiceNickDest] = useState('');
  const [invoiceNickReason, setInvoiceNickReason] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceReason, setInvoiceReason] = useState('');
  const [invoiceError, setInvoiceError] = useState('');

  // Состояния для дешифратора
  const [decryptId, setDecryptId] = useState('');
  const [decryptResult, setDecryptResult] = useState(null);
  const [decryptError, setDecryptError] = useState('');

  // Сортировка счетов
  const [accSort, setAccSort] = useState('number_asc');
  const [accSearch, setAccSearch] = useState('');
  const [accSearchFocus, setAccSearchFocus] = useState(false);
  
  const [toast, setToast] = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  const copyToClipboard = (text) => {
    copyToClipboardUtil(text, () => showToastMsg('Скопировано'), () => showToastMsg('Ошибка копирования', true));
  };

  const showToastMsg = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    if (tab === 'general') {
      if (subTab === 'main') {
        try {
          const res = await fetch('/api/admin/stats');
          const data = await res.json();
          setStats(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
        } catch (err) {
          console.error('Ошибка загрузки статистики', err);
        }
      } else if (subTab === 'players') {
        try {
          const res = await fetch('/api/admin/players');
          const data = await res.json();
          setPlayers(prev => JSON.stringify(prev) !== JSON.stringify(data.players || []) ? data.players || [] : prev);
        } catch (err) {
          console.error('Ошибка загрузки игроков', err);
        }
      } else if (subTab === 'system') {
        try {
          const res = await fetch('/api/admin/settings');
          const data = await res.json();
          setMaintenance(data.maintenance);
          setAllowUnlinkedLogin(!!data.allow_unlinked_login);
          setNotificationChatId(data.notification_chat_id || '');
          setWarehouseChatId(data.warehouse_chat_id || '');
          setBackupChatId(data.backup_chat_id || '');
          setTreasuryAccount(prev => (savingTreasury ? prev : (data.treasury_account_number || '')));
          const resAcc = await fetch('/api/admin/accounts');
          const dataAcc = await resAcc.json();
          setAccounts(prev => JSON.stringify(prev) !== JSON.stringify(dataAcc.accounts || []) ? dataAcc.accounts || [] : prev);
        } catch (err) {
          console.error('Ошибка загрузки настроек', err);
        }
      }
    } else if (tab === 'market') {
      if (marketSubTab === 'overview') {
        try {
          const res = await fetch('/api/admin/market-items');
          const data = await res.json();
          setMarketItems(prev => JSON.stringify(prev) !== JSON.stringify(data.items || []) ? data.items || [] : prev);
        } catch (err) {
          console.error('Ошибка загрузки товаров', err);
        }
      } else if (marketSubTab === 'ads') {
        try {
          const resStats = await fetch('/api/admin/ads/stats');
          const statsData = await resStats.json();
          setAdStats(statsData);
          const res = await fetch('/api/admin/ads');
          const data = await res.json();
          setAdminAds(data.ads || []);
          const resAcc = await fetch('/api/admin/accounts');
          const dataAcc = await resAcc.json();
          setAccounts(prev => JSON.stringify(prev) !== JSON.stringify(dataAcc.accounts || []) ? dataAcc.accounts || [] : prev);
        } catch (err) {
          console.error('Ошибка загрузки рекламы', err);
        }
      }
    } else if (tab === 'bank') {
      if (bankSubTab === 'accounts') {
        try {
          const res = await fetch('/api/admin/accounts');
          const data = await res.json();
          setAccounts(data.accounts || []);
        } catch (err) {
          console.error('Ошибка загрузки счетов', err);
        }
      } else if (bankSubTab === 'invoices') {
        try {
          const res = await fetch('/api/admin/invoices');
          const data = await res.json();
          setAllInvoices(prev => JSON.stringify(prev) !== JSON.stringify(data.invoices || []) ? data.invoices || [] : prev);
          const resP = await fetch('/api/admin/players');
          const dataP = await resP.json();
          setPlayers(prev => JSON.stringify(prev) !== JSON.stringify(dataP.players || []) ? dataP.players || [] : prev);
          const resA = await fetch('/api/admin/accounts');
          const dataA = await resA.json();
          setAccounts(prev => JSON.stringify(prev) !== JSON.stringify(dataA.accounts || []) ? dataA.accounts || [] : prev);
        } catch (err) {
          console.error('Ошибка загрузки штрафов', err);
        }
      }
    }
  }, [tab, subTab, bankSubTab, marketSubTab, savingTreasury]);

  const fetchAdminHistory = useCallback(async (page = 1, limit = 50) => {
    if (!adminHistoryView.tgId) return;
    try {
      const res = await fetch(`/api/bank/history/${adminHistoryView.tgId}?page=${page}&limit=${limit}&type=${adminFilterType}&account=${adminFilterAccount}`);
      const json = await res.json();
      if (!res.ok) {
        setAdminHistoryError(json.error || 'Не удалось загрузить историю операций');
        setAdminHistory([]);
        return;
      }
      setAdminHistoryError('');
      setAdminHistory(prev => JSON.stringify(prev) !== JSON.stringify(json.history || []) ? json.history || [] : prev);
    } catch (err) {
      console.error('Ошибка загрузки истории:', err);
      setAdminHistoryError('Ошибка соединения с сервером');
      setAdminHistory([]);
    }
  }, [adminHistoryView.tgId, adminFilterType, adminFilterAccount]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (tab === 'market' && marketSubTab === 'ads') {
      fetch('/api/admin/settings')
        .then(res => res.json())
        .then(settingsData => {
          setAdPrice(settingsData.ad_price);
          setAdCardNumber(settingsData.ad_card_number || '');
          setPromotionPricePerDay(settingsData.promotion_price_per_day);
          setPromotionLabelPrice(settingsData.promotion_label_price);
          setCardDesignPrice(settingsData.card_design_price);
        })
        .catch(err => console.error('Ошибка загрузки настроек рекламы', err));
    }
  }, [tab, marketSubTab]);

  useEffect(() => {
    if (adminHistoryView.show) {
      fetchAdminHistory(adminHistoryPage, 50);
    }
  }, [adminHistoryView.show, adminHistoryPage, fetchAdminHistory]);

  // Приход из «Профиля действий»: открываем историю операций нужного игрока и
  // помечаем целевую операцию (по её req_id) для подсветки/прокрутки.
  useEffect(() => {
    if (!initialFocus) return;
    setAdminHistoryView({ show: true, tgId: initialFocus.tgId, accountNum: initialFocus.accountNum, accountId: initialFocus.accountId });
    setAdminFilterType('ALL');
    setAdminFilterAccount('ALL');
    setAdminHistoryPage(1);
    setAdminHistoryError('');
    setHighlightOps(Array.isArray(initialFocus.matchIds) ? initialFocus.matchIds.map(String) : []);
    onFocusConsumed && onFocusConsumed();
  }, [initialFocus, onFocusConsumed]);

  // Приход из «Профиля действий» по клику на #ID заявки: открываем нужный раздел
  // заявок (вывод/пополнение/заказы/склад) и просим дочерний компонент
  // прокрутиться к этой заявке и подсветить её.
  useEffect(() => {
    if (!requestFocus) return;
    const { section, requestId, nonce } = requestFocus;
    // Уходим из режима истории операций счёта, если он был открыт.
    setAdminHistoryView({ show: false, tgId: null, accountNum: null, accountId: null });
    if (section === 'bank-withdrawals') {
      setTab('bank'); setBankSubTab('withdrawals'); setReqFocus({ scope: 'bank', requestId: String(requestId), nonce });
    } else if (section === 'bank-deposits') {
      setTab('bank'); setBankSubTab('deposits'); setReqFocus({ scope: 'bank', requestId: String(requestId), nonce });
    } else if (section === 'market-orders') {
      setTab('market'); setMarketSubTab('orders'); setReqFocus({ scope: 'market', requestId: String(requestId), nonce });
    } else if (section === 'market-requests') {
      setTab('market'); setMarketSubTab('requests'); setReqFocus({ scope: 'market', requestId: String(requestId), nonce });
    }
    onRequestFocusConsumed && onRequestFocusConsumed();
  }, [requestFocus, onRequestFocusConsumed]);

  // Как только подгрузилась история с подсвеченной операцией — прокручиваем к ней
  // и через несколько секунд снимаем подсветку.
  useEffect(() => {
    if (highlightOps.length && highlightItemRef.current) {
      highlightItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const t = setTimeout(() => setHighlightOps([]), 3000);
      return () => clearTimeout(t);
    }
  }, [adminHistory, highlightOps]);

  const isFilterActive = filters.account !== 'ALL' || filters.balance !== 'ALL' || filters.status !== 'ALL';

  const filteredPlayers = players.filter(p => {
    const matchesSearch = p.mc_nickname.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    
    if (filters.account === 'HAS_ACCOUNT' && p.has_account === 0) return false;
    if (filters.account === 'NO_ACCOUNT' && p.has_account === 1) return false;
    if (filters.balance === 'POSITIVE' && p.balance <= 0) return false;
    if (filters.status !== 'ALL' && p.account_status !== filters.status) return false;
    
    return true;
  });

  const searchedAccounts = accounts.filter(a => {
    const q = accSearch.trim().toLowerCase();
    if (!q) return true;
    return String(a.account_number).includes(q) || (a.mc_nickname || '').toLowerCase().includes(q);
  });

  const sortedAccounts = [...searchedAccounts].sort((a, b) => {
    if (accSort === 'number_asc') return a.account_number - b.account_number;
    if (accSort === 'number_desc') return b.account_number - a.account_number;
    if (accSort === 'balance_asc') return a.balance - b.balance;
    if (accSort === 'balance_desc') return b.balance - a.balance;
    return 0;
  });

  const formatTime = (utcStr) => {
    if (!utcStr) return 'Нет данных';
    const date = new Date(utcStr.replace(' ', 'T') + 'Z');
    return date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', dateStyle: 'short', timeStyle: 'short' });
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setFilterModal(false);
  };

  const resetFilters = () => {
    const def = { account: 'ALL', balance: 'ALL', status: 'ALL' };
    setFilters(def);
    setTempFilters(def);
    setFilterModal(false);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAction = async (targetTgId, action, reason = '') => {
    setBanError('');
    if (action === 'ban' && !reason.trim()) {
      setBanError('Причина обязательна');
      return;
    }
    try {
      const res = await fetch('/api/admin/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, target_tg_id: targetTgId, action, reason })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Ошибка выполнения действия', true);
      } else {
        setBanModal(false);
        setBanReason('');
        setBanTarget(null);
        fetchData();
      }
    } catch (err) {
      setBanError('Ошибка соединения с сервером');
    }
  };

  const handleToggleRole = async (targetTgId, roleKey, currentRoles) => {
    const newRoles = currentRoles.includes(roleKey) ? currentRoles.filter(r => r !== roleKey) : [...currentRoles, roleKey];
    try {
      const res = await fetch('/api/admin/roles/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, target_tg_id: targetTgId, roles: newRoles })
      });
      if (res.ok) {
        setPlayers(prev => prev.map(pl => String(pl.tg_id) === String(targetTgId) ? { ...pl, roles: newRoles } : pl));
      } else {
        showToastMsg((await res.json()).error || 'Ошибка', true);
      }
    } catch (e) { showToastMsg('Ошибка соединения', true); }
  };

  const handleAccountActionClick = (targetTgId, accountNum, action) => {
    if (action === 'unblock') {
      executeAccountAction(targetTgId, accountNum, action, '');
    } else {
      setAccActionModal({ show: true, action, tgId: targetTgId, accountNum });
    }
  };

  const executeAccountAction = async (targetTgId, accountNum, action, reason) => {
    setAccActionError('');
    if ((action === 'block' || action === 'delete') && !reason.trim()) {
      setAccActionError('Причина обязательна');
      return;
    }
    try {
      const res = await fetch('/api/admin/account-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, target_tg_id: targetTgId, account_number: accountNum, action, reason })
      });
      if (res.ok) {
        setAccActionModal({ show: false, action: '', tgId: null, accountNum: null });
        setAccActionReason('');
        fetchData();
      } else {
        setAccActionError((await res.json()).error);
      }
    } catch (e) { setAccActionError('Ошибка соединения'); }
  };

  const saveAccSettings = async () => {
    try {
      const res = await fetch('/api/admin/account-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, target_tg_id: accEditData.tgId, ...accEditData, remove_image: accDesignRemoved })
      });
      if (res.ok) {
        setAccSettingsModal(false);
        fetchData();
      } else {
        showToastMsg((await res.json()).error, true);
      }
    } catch (e) { console.error(e); }
  };

  const handleAccDesignFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      showToastMsg('Файл слишком большой (макс 3МБ)', true);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setAccEditData(prev => ({ ...prev, image: reader.result }));
      setAccDesignRemoved(false);
    };
    reader.readAsDataURL(file);
  };

  const handleAccDesignRemove = () => {
    setAccEditData(prev => ({ ...prev, image: '' }));
    setAccDesignRemoved(true);
  };

  const openAccSettings = (acc) => {
    setAccEditData({
      tgId: acc.tg_id,
      originalNumber: acc.account_number,
      number: acc.account_number,
      name: acc.account_name,
      balance: acc.balance,
      color: acc.account_color,
      image: acc.card_image_url || ''
    });
    setAccDesignRemoved(false);
    setAccSettingsModal(true);
  };



  const handleUpdateMarketItem = async () => {
    if (!editItemModal) return;
    try {
      const res = await fetch(`/api/admin/market-items/${editItemModal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, ...editItemModal })
      });
      if (res.ok) {
        showToastMsg('Товар обновлен');
        setEditItemModal(null);
        fetchData();
      } else {
        showToastMsg((await res.json()).error, true);
      }
    } catch (e) { showToastMsg('Ошибка соединения', true); }
  };

  const handleDeleteMarketItem = async () => {
    if (!deleteItemModal) return;
    if (!deleteItemReason.trim()) { setDeleteItemError('Укажите причину удаления'); return; }
    setIsDeletingItem(true);
    try {
      const res = await fetch(`/api/admin/market-items/${deleteItemModal.id}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, reason: deleteItemReason })
      });
      const data = await res.json();
      if (res.ok) {
        showToastMsg('Товар удалён, ячейка освобождена');
        setDeleteItemModal(null);
        setDeleteItemReason('');
        setDeleteItemError('');
        fetchData();
      } else {
        setDeleteItemError(data.error || 'Ошибка удаления товара');
      }
    } catch (e) {
      setDeleteItemError('Ошибка соединения');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleSaveChatId = async () => {
    try {
      const res = await fetch('/api/admin/settings/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, chat_id: notificationChatId })
      });
      if (res.ok) showToastMsg('ID чата сохранен!');
    } catch (e) { console.error(e); }
  };

  const handleSaveWarehouseChatId = async () => {
    try {
      const res = await fetch('/api/admin/settings/warehouse-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, chat_id: warehouseChatId })
      });
      if (res.ok) showToastMsg('ID чата склада сохранен!');
    } catch (e) { console.error(e); }
  };

  const handleSaveBackupChatId = async () => {
    try {
      const res = await fetch('/api/admin/settings/backup-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, chat_id: backupChatId })
      });
      if (res.ok) showToastMsg('ID чата бэкапов сохранен!');
    } catch (e) { console.error(e); }
  };

  const handleCreateBackupNow = async () => {
    setIsBackingUp(true);
    try {
      const res = await fetch('/api/admin/backup/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId })
      });
      if (res.ok) {
        showToastMsg('Бэкап отправлен в чат');
      } else {
        showToastMsg((await res.json()).error || 'Не удалось отправить бэкап', true);
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setIsBackingUp(false);
    }
  };

  const backupFileInputRef = useRef(null);

  const handleRestoreBackupFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!/\.db$/i.test(file.name)) {
      showToastMsg('Выберите файл с расширением .db', true);
      return;
    }

    const confirmed = window.confirm('Текущая база данных будет заменена загруженным файлом, сервер перезапустится. Продолжить?');
    if (!confirmed) return;

    setIsRestoringBackup(true);
    try {
      const fileBuffer = await file.arrayBuffer();
      const query = new URLSearchParams({ admin_tg_id: tgId, filename: file.name });
      const res = await fetch(`/api/admin/backup/restore?${query.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: fileBuffer
      });
      if (res.ok) {
        showToastMsg('База данных заменена, сервер перезапускается...');
      } else {
        showToastMsg((await res.json()).error || 'Не удалось загрузить бэкап', true);
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setIsRestoringBackup(false);
    }
  };

  // Открывает карточку товара (форму редактирования) прямо из дешифратора
  // квитанций по клику на номер ячейки в составе заказа.
  const jumpToItemInAdmin = async (itemId) => {
    try {
      const res = await fetch('/api/admin/market-items');
      const data = await res.json();
      const found = (data.items || []).find(i => String(i.id) === String(itemId));
      if (!found) { showToastMsg('Товар не найден', true); return; }
      setMarketItems(data.items || []);
      setTab('market');
      setMarketSubTab('overview');
      setEditItemModal({ ...found });
    } catch (e) {
      showToastMsg('Не удалось открыть карточку товара', true);
    }
  };

  const handleDeleteAd = async (id) => {
    try {
      const res = await fetch(`/api/admin/ads/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId })
      });
      if (res.ok) {
        showToastMsg('Баннер удален');
        setDeleteAdModal(null);
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const handleUpdateAd = async () => {
    try {
      const res = await fetch(`/api/admin/ads/${editAdModal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, ...editAdModal })
      });
      if (res.ok) {
        showToastMsg('Баннер обновлен');
        setEditAdModal(null);
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const handleSaveTreasury = async () => {
    if (savingTreasury) return;
    setSavingTreasury(true);
    try {
      const res = await fetch('/api/admin/settings/treasury', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, account_number: treasuryAccount })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Не удалось сохранить счёт казны', true);
      } else {
        showToastMsg(treasuryAccount === '' ? 'Счёт казны снят' : 'Счёт казны назначен');
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setSavingTreasury(false);
      fetchData();
    }
  };

  const handleSaveAdAndPromotionSettings = async () => {
    try {
      const [res1, res2] = await Promise.all([
        fetch('/api/admin/settings/ad_price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_tg_id: tgId, price: adPrice, card_number: adCardNumber })
        }),
        fetch('/api/admin/settings/promotion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_tg_id: tgId, price_per_day: promotionPricePerDay, label_price: promotionLabelPrice, card_design_price: cardDesignPrice })
        })
      ]);
      if (res1.ok && res2.ok) showToastMsg('Настройки сохранены!');
      else showToastMsg('Ошибка сохранения настроек', true);
    } catch (e) { console.error(e); }
  };

  const handleInvoiceByNick = async () => {
    if (!invoiceNick || !invoiceNickAmount || !invoiceNickDest || !invoiceNickReason) return showToastMsg('Заполните все поля', true);
    try {
      const res = await fetch('/api/admin/invoice-by-nick', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, target_nickname: invoiceNick, amount: invoiceNickAmount, destination_account: invoiceNickDest, reason: invoiceNickReason })
      });
      if (res.ok) {
        setInvoiceNick(''); setInvoiceNickAmount(''); setInvoiceNickDest(''); setInvoiceNickReason('');
        showToastMsg('Инвойс успешно выставлен!');
        fetchData();
      } else {
        showToastMsg((await res.json()).error, true);
      }
    } catch (e) { showToastMsg('Ошибка соединения', true); }
  };

  const handleInvoice = async () => {
    setInvoiceError('');
    if (!invoiceAmount || invoiceAmount <= 0) return setInvoiceError('Укажите корректную сумму');
    if (!invoiceDestAccount || invoiceDestAccount.trim() === '') return setInvoiceError('Укажите счет зачисления');
    if (!invoiceReason || invoiceReason.trim() === '') return setInvoiceError('Укажите причину');

    try {
      const res = await fetch('/api/admin/invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, target_tg_id: invoiceTarget.tgId, amount: invoiceAmount, reason: invoiceReason, destination_account: invoiceDestAccount })
      });
      if (res.ok) {
        setInvoiceModal(false);
        setInvoiceTarget(null);
        setInvoiceAmount('');
        setInvoiceReason('');
        setInvoiceDestAccount('');
        showToastMsg('Инвойс успешно выставлен!');
      } else {
        setInvoiceError((await res.json()).error);
      }
    } catch (e) { setInvoiceError('Ошибка соединения'); }
  };

  const toggleMaintenance = async () => {
    try {
      const res = await fetch('/api/admin/settings/maintenance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, maintenance: !maintenance })
      });
      if (res.ok) {
        setMaintenance(!maintenance);
        showToastMsg(`Тех. работы ${!maintenance ? 'включены' : 'выключены'}`);
      }
    } catch (e) { console.error(e); }
  };

  const toggleAllowUnlinkedLogin = async () => {
    try {
      const res = await fetch('/api/admin/settings/allow-unlinked-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, allow_unlinked_login: !allowUnlinkedLogin })
      });
      if (res.ok) {
        setAllowUnlinkedLogin(!allowUnlinkedLogin);
        showToastMsg(`Вход без привязки ника ${!allowUnlinkedLogin ? 'включён' : 'выключен'}`);
      }
    } catch (e) { console.error(e); }
  };

  const sendAnnounce = async () => {
    if (!announceText.trim() && announceMedia.length === 0) {
      return showToastMsg('Добавьте текст или вложение', true);
    }
    setIsAnnouncing(true);
    try {
      const res = await fetch('/api/admin/announce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_tg_id: tgId,
          text: announceText,
          media: announceMedia.map(m => ({ kind: m.kind, data_base64: m.data_base64 }))
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setAnnounceText('');
        setAnnounceMedia([]);
        showToastMsg(`Рассылка запущена! Получателей: ${data.count}`);
      } else {
        showToastMsg(data.error || 'Не удалось запустить рассылку', true);
      }
    } catch (e) {
      console.error(e);
      showToastMsg('Ошибка соединения', true);
    } finally {
      setIsAnnouncing(false);
    }
  };

  // Определяем тип вложения по MIME: gif → animation, video/* → video, иначе photo.
  const detectMediaKind = (file) => {
    if (file.type === 'image/gif') return 'animation';
    if (file.type.startsWith('video/')) return 'video';
    return 'photo';
  };

  const handleAnnounceFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // чтобы повторный выбор того же файла тоже срабатывал
    if (!files.length) return;
    const room = 10 - announceMedia.length;
    if (room <= 0) return showToastMsg('Можно прикрепить не больше 10 файлов', true);
    const toAdd = files.slice(0, room);
    if (files.length > room) showToastMsg('Добавлены не все — лимит 10 файлов', true);

    toAdd.forEach(file => {
      // Видео до 20МБ (лимит Telegram для загрузки ботом), фото/гиф до 10МБ.
      const isVideo = file.type.startsWith('video/');
      const maxMb = isVideo ? 20 : 10;
      if (file.size > maxMb * 1024 * 1024) {
        showToastMsg(`«${file.name}» слишком большой (макс ${maxMb}МБ)`, true);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAnnounceMedia(prev => {
          if (prev.length >= 10) return prev;
          // Суммарный размер вложений держим под лимитом тела запроса (60МБ на
          // сервере, см. express.json). base64 раздувает ~на треть, поэтому
          // ориентируемся на ~40МБ исходных данных суммарно.
          const usedBytes = prev.reduce((s, m) => s + Math.floor((m.data_base64.length * 3) / 4), 0);
          const addBytes = Math.floor((reader.result.length * 3) / 4);
          if (usedBytes + addBytes > 40 * 1024 * 1024) {
            showToastMsg('Суммарный размер вложений слишком большой (макс ~40МБ)', true);
            return prev;
          }
          return [...prev, { kind: detectMediaKind(file), data_base64: reader.result, name: file.name }];
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAnnounceMedia = (idx) => {
    setAnnounceMedia(prev => prev.filter((_, i) => i !== idx));
  };

  // Оборачивает выделенный в textarea текст HTML-тегами Telegram. Если ничего не
  // выделено — вставляет теги и ставит курсор между ними. openTag/closeTag —
  // строки тегов; после вставки восстанавливаем фокус и выделение.
  const wrapAnnounceSelection = (openTag, closeTag) => {
    const ta = announceTextRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = announceText;
    const selected = value.slice(start, end);
    const newValue = value.slice(0, start) + openTag + selected + closeTag + value.slice(end);
    setAnnounceText(newValue);
    // Возвращаем фокус и ставим курсор/выделение внутри вставленных тегов.
    requestAnimationFrame(() => {
      ta.focus();
      const innerStart = start + openTag.length;
      const innerEnd = innerStart + selected.length;
      ta.setSelectionRange(innerStart, innerEnd);
    });
  };

  // Кнопки Telegram-разметки. Порядок как в самом Telegram (см. меню форматирования).
  const ANNOUNCE_FORMAT_BTNS = [
    { label: 'Ж', title: 'Жирный', style: { fontWeight: 800 }, open: '<b>', close: '</b>' },
    { label: 'К', title: 'Курсив', style: { fontStyle: 'italic' }, open: '<i>', close: '</i>' },
    { label: 'П', title: 'Подчёркнутый', style: { textDecoration: 'underline' }, open: '<u>', close: '</u>' },
    { label: 'З', title: 'Зачёркнутый', style: { textDecoration: 'line-through' }, open: '<s>', close: '</s>' },
    { label: '❝', title: 'Цитата', style: {}, open: '<blockquote>', close: '</blockquote>' },
    { label: '{ }', title: 'Моноширинный', style: { fontFamily: 'monospace' }, open: '<code>', close: '</code>' },
    { label: '▮', title: 'Скрытый (спойлер)', style: {}, open: '<tg-spoiler>', close: '</tg-spoiler>' },
  ];

  const applyAnnounceLink = () => {
    const url = (linkModal && linkModal.url || '').trim();
    if (!url) return setLinkModal(null);
    // Экранируем кавычки в URL, чтобы не сломать атрибут href.
    const safeUrl = url.replace(/"/g, '&quot;');
    wrapAnnounceSelection(`<a href="${safeUrl}">`, '</a>');
    setLinkModal(null);
  };

  const handleDecrypt = async () => {
    setDecryptError('');
    setDecryptResult(null);
    if (!decryptId.trim()) return;
    try {
      const res = await fetch('/api/admin/decrypt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: decryptId.trim(), admin_tg_id: tgId })
      });
      const data = await res.json();
      if (!res.ok) {
        setDecryptError(data.error || 'Не найдено');
      } else {
        setDecryptResult(data.operation);
      }
    } catch (e) { setDecryptError('Ошибка соединения'); }
  };

  const getAccountCardStyle = (status) => {
    if (status === 'BLOCKED') return { background: 'var(--danger-soft)', border: '1px solid var(--danger)' };
    if (status === 'DELETED') return { background: 'var(--bg-subtle)', opacity: 0.8 };
    return {};
  };

  const openReceipt = (item) => {
    const accNumber = adminFilterAccount !== 'ALL' ? adminHistoryView.accountNum : undefined;
    setReceiptData(buildReceiptData(item, adminHistoryView.tgId, accNumber));
  };



  if (adminHistoryView.show) {
    return (
      <div>
        <button className="secondary-btn" onClick={() => {
          setAdminHistoryView({ show: false, tgId: null, accountNum: null, accountId: null });
          setAdminFilterType('ALL');
          setAdminFilterAccount('ALL');
          setAdminHistoryPage(1);
          setAdminHistoryError('');
        }} style={{marginBottom: '15px', width: 'auto', padding: '10px 15px'}}>← Назад к счетам</button>
        <h2 style={{marginTop: 0}}>История операций (Счет #{adminHistoryView.accountNum})</h2>
        
        <div className="history-filters">
          <select value={adminFilterType} onChange={e => { setAdminFilterType(e.target.value); setAdminHistoryPage(1); window.scrollTo(0,0); }}>
            <option value="ALL">Все операции</option>
            <option value="TRANSFER_OUT">Все списания</option>
            <option value="TRANSFER_IN">Все поступления</option>
            <option value="CREATE_ACCOUNT">Создание счета</option>
            <option value="UPDATE_ACCOUNT">Изменение настроек</option>
            <option value="DELETE_ACCOUNT">Удаление счета</option>
          </select>
          <select value={adminFilterAccount} onChange={e => { setAdminFilterAccount(e.target.value); setAdminHistoryPage(1); window.scrollTo(0,0); }}>
            <option value="ALL">Все счета</option>
            <option value={adminHistoryView.accountId}>Счет #{adminHistoryView.accountNum}</option>
          </select>
        </div>

        <div className="card">
          {adminHistoryError ? (
            <p style={{textAlign: 'center', color: 'var(--danger)'}}>⚠ {adminHistoryError}</p>
          ) : adminHistory.length === 0 ? (
            <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>История пуста</p>
          ) : adminHistory.map(item => {
            const isHit = highlightOps.length > 0 && item.req_id != null && highlightOps.includes(String(item.req_id));
            return (
            <div key={item.unique_id} ref={isHit ? highlightItemRef : null} className={isHit ? 'op-history-highlight' : undefined}>
            <HistoryItem
              item={item}
              currentTgId={adminHistoryView.tgId}
              currentAccountNumber={adminFilterAccount !== 'ALL' ? adminHistoryView.accountNum : undefined}
              isDeletedAccountOp={item.account_status === 'DELETED'}
              onReceiptClick={openReceipt}
            />
            </div>
            );
          })}
        </div>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <button className="secondary-btn" style={{width: 'auto', padding: '10px 20px'}} disabled={adminHistoryPage === 1} onClick={() => { setAdminHistoryPage(p => p - 1); window.scrollTo(0,0); }}>←</button>
          <span style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Страница {adminHistoryPage}</span>
          <button className="secondary-btn" style={{width: 'auto', padding: '10px 20px'}} disabled={adminHistory.length < 50} onClick={() => { setAdminHistoryPage(p => p + 1); window.scrollTo(0,0); }}>→</button>
        </div>

        <ReceiptModal receiptData={receiptData} onClose={() => setReceiptData(null)} onCopyReceipt={copyToClipboard} />
      </div>
    );
  }

  return (
    <div>
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}

      <div className="admin-tabs">
        <button className={tab === 'general' ? 'active' : ''} onClick={() => setTab('general')}>Общее</button>
        <button className={tab === 'bank' ? 'active' : ''} onClick={() => setTab('bank')}>Банк</button>
        <button className={tab === 'market' ? 'active' : ''} onClick={() => setTab('market')}>Маркетплейс</button>
      </div>

      {tab === 'general' && (
        <div key="tab-general" className="tab-fade">
          <div className="sub-tabs">
            <button className={subTab === 'main' ? 'active' : ''} onClick={() => setSubTab('main')}>Главная</button>
            <button className={subTab === 'players' ? 'active' : ''} onClick={() => setSubTab('players')}>Игроки</button>
            <button className={subTab === 'system' ? 'active' : ''} onClick={() => setSubTab('system')}>Система</button>
          </div>

          {subTab === 'main' && !stats && (
            <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>
          )}

          {subTab === 'main' && stats && (
            <div key="sub-main" className="tab-fade">
              <div className="card">
                <h3 style={{marginTop: 0}}>Пользователи</h3>
                <p>Общее количество игроков: <b>{stats.totalPlayers}</b></p>
                <p>Сейчас в приложении: <b style={{color: 'var(--success)'}}>{stats.onlineNow}</b></p>
              </div>
              <div className="card">
                <h3 style={{marginTop: 0}}>Банковская система</h3>
                <p>Активных счетов: <b>{stats.activeAccounts}</b></p>
                <p>Заблокированных счетов: <b style={{color: 'var(--danger)'}}>{stats.blockedAccounts}</b></p>
                <p>Удаленных счетов: <b>{stats.deletedAccounts}</b></p>
                <p>Всего средств на счетах: <b style={{color: 'var(--accent-dark)'}}>{stats.totalMoney} сц</b></p>
                {stats.frozenOrderFunds > 0 && (
                  <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '-8px'}}>из них заморожено по заказам в сборке: {stats.frozenOrderFunds} сц</p>
                )}
              </div>
            </div>
          )}

          {subTab === 'players' && (
            <div key="sub-players" className="tab-fade">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <span style={{fontSize: '14px', color: 'var(--text-secondary)'}}>
                  {isFilterActive ? `Подходит под условия: ${filteredPlayers.length}` : `Всего игроков: ${players.length}`}
                </span>
                <button className="link-btn" onClick={() => { setTempFilters(filters); setFilterModal(true); }}>
                  {isFilterActive ? 'Фильтры (Активны)' : 'Фильтры'}
                </button>
              </div>

              <input 
                type="text" 
                placeholder="Поиск по никнейму..." 
                value={search || ''} 
                onChange={e => setSearch(e.target.value)} 
              />

              {filteredPlayers.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Игроки не найдены</p>
              ) : (
                filteredPlayers.map(p => {
                  const isBanned = p.is_banned === 1;
                  const warns = p.warns || 0;
                  let nameClass = 'player-name';
                  if (isBanned) nameClass += ' banned-player';
                  else if (warns > 0) nameClass += ' warned-player';

                  const isSelf = String(p.tg_id) === String(tgId);
                  const isSuperAdmin = String(tgId) === '1567618440';
                  const canPunish = !isSelf && (!p.is_admin || isSuperAdmin);

                  return (
                    <div key={p.tg_id} className="card" onClick={() => toggleExpand(p.tg_id)} style={{cursor: 'pointer'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <PlayerAvatar nickname={p.mc_nickname} size={26} />
                          <span className={nameClass}>{p.mc_nickname} {warns > 0 && !isBanned ? `(${warns}/3)` : ''}</span>
                        </span>
                        {p.tg_username && p.tg_username !== 'Без имени' ? (
                          <a 
                            href={`https://t.me/${p.tg_username.replace('@', '')}`} 
                            target="_blank" 
                            rel="noreferrer" 
                            onClick={e => e.stopPropagation()}
                            style={{color: 'var(--accent-dark)', textDecoration: 'none', fontSize: '14px'}}
                          >
                            {p.tg_username}
                          </a>
                        ) : <span style={{color: 'var(--text-secondary)', fontSize: '14px'}}>Нет юза</span>}
                      </div>
                      
                      {expandedIds.includes(p.tg_id) && (
                        <div style={{marginTop: '15px', fontSize: '14px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', paddingTop: '10px', lineHeight: '1.6'}}>
                          <p style={{margin: '5px 0'}}>
                            <b>Счета:</b> {p.accounts && p.accounts.length > 0 ? p.accounts.map((a, idx) => (
                              <span key={a.id}>
                                <span
                                  style={{color: a.status === 'BLOCKED' ? 'var(--danger)' : 'var(--accent-dark)', cursor: 'pointer', textDecoration: 'underline'}}
                                  onClick={(e) => { e.stopPropagation(); setTab('bank'); setBankSubTab('accounts'); setExpandedAccountId(a.id); }}
                                >
                                  #{a.account_number}{a.status === 'BLOCKED' ? ' (заблокирован)' : ''}
                                </span>
                                {idx < p.accounts.length - 1 ? ', ' : ''}
                              </span>
                            )) : 'Нет счетов'}
                          </p>
                          {p.accounts && p.accounts.length > 0 && <p style={{margin: '5px 0'}}><b>Общий баланс:</b> {p.balance} сц</p>}
                          <p style={{margin: '5px 0'}}><b>Первый вход:</b> {formatTime(p.created_at)}</p>
                          <p style={{margin: '5px 0'}}><b>Последний вход:</b> {formatTime(p.last_login)}</p>
                          <p style={{margin: '5px 0'}}><b>Почтовый адрес:</b> {p.postal_address || 'Не указан'}</p>
                          <p style={{margin: '5px 0'}}>
                            <b>Роли:</b> {[p.is_admin ? 'Администратор' : null, ...(p.roles || []).map(r => ROLE_LABELS[r])].filter(Boolean).join(', ') || 'Игрок'}
                          </p>

                          {isAdmin && !p.is_admin && (
                            <div style={{marginTop: '8px'}}>
                              <p style={{margin: '5px 0', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-secondary)'}}>Управление ролями:</p>
                              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                {ASSIGNABLE_ROLES.map(roleKey => {
                                  const active = (p.roles || []).includes(roleKey);
                                  return (
                                    <button
                                      key={roleKey}
                                      className={active ? 'primary-btn' : 'secondary-btn'}
                                      style={{width: 'auto', padding: '6px 12px', fontSize: '13px', flex: 'none'}}
                                      onClick={(e) => { e.stopPropagation(); handleToggleRole(p.tg_id, roleKey, p.roles || []); }}
                                    >
                                      {ROLE_LABELS[roleKey]}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {isBanned && (
                            <>
                              <p style={{margin: '5px 0', color: 'var(--danger)'}}><b>Причина бана:</b> {p.ban_reason || 'Не указана'}</p>
                              <p style={{margin: '5px 0', color: 'var(--danger)'}}><b>Забанил:</b> {p.banned_by || 'Администратор'}</p>
                            </>
                          )}
                          
                          {isAdmin && canPunish && (
                            <div style={{marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                              <button className="secondary-btn" style={{padding: '8px', flex: '1 1 30%'}} onClick={(e) => { e.stopPropagation(); handleAction(p.tg_id, 'warn_add'); }}>+ Варн</button>
                              <button className="secondary-btn" style={{padding: '8px', flex: '1 1 30%'}} onClick={(e) => { e.stopPropagation(); handleAction(p.tg_id, 'warn_remove'); }}>- Варн</button>
                              <button className="secondary-btn" style={{padding: '8px', flex: '1 1 30%', color: isBanned ? 'var(--success)' : 'var(--danger)'}} onClick={(e) => { 
                                e.stopPropagation(); 
                                if (isBanned) {
                                  handleAction(p.tg_id, 'unban');
                                } else {
                                  setBanTarget(p.tg_id);
                                  setBanModal(true);
                                }
                              }}>
                                {isBanned ? 'Разбанить' : 'Забанить'}
                              </button>

                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {subTab === 'system' && (
            <div key="sub-system" className="tab-fade">
              <div className="card">
                <h3 style={{marginTop: 0}}>Управление системой</h3>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                  <span style={{fontSize: '14px', fontWeight: 'bold'}}>Технические работы: {maintenance ? 'Включены' : 'Выключены'}</span>
                  {isAdmin && (
                    <button 
                      className={maintenance ? "primary-btn" : "secondary-btn"} 
                      style={{width: 'auto', background: maintenance ? 'var(--danger)' : '', color: maintenance ? '#fff' : undefined}} 
                      onClick={toggleMaintenance}
                    >
                      {maintenance ? 'Выключить' : 'Включить'}
                    </button>
                  )}
                </div>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 15px 0'}}>При включении обычные игроки не смогут зайти в приложение.</p>

                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                  <span style={{fontSize: '14px', fontWeight: 'bold'}}>Вход без привязки ника: {allowUnlinkedLogin ? 'Включён' : 'Выключен'}</span>
                  {isAdmin && (
                    <button 
                      className={allowUnlinkedLogin ? "primary-btn" : "secondary-btn"} 
                      style={{width: 'auto', background: allowUnlinkedLogin ? 'var(--accent)' : '', color: allowUnlinkedLogin ? '#fff' : undefined}} 
                      onClick={toggleAllowUnlinkedLogin}
                    >
                      {allowUnlinkedLogin ? 'Выключить' : 'Включить'}
                    </button>
                  )}
                </div>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', margin: 0}}>При включении в приложение смогут заходить и игроки без привязанного в API ника Minecraft — им автоматически ставится ник вида их Telegram ID. При выключении такие игроки (даже уже заходившие раньше) снова не смогут войти, пока не привяжут ник.</p>
              </div>

              <div className="card">
                <h3 style={{marginTop: 0}}>Счёт казны</h3>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>
                  Назначьте счёт казны из уже созданных счетов. Он станет доступен только администраторам
                  и появится у них последним в списке счетов, а у прежнего владельца пропадёт. В лимит счетов
                  игрока он не входит. С него можно только переводить средства; название, цвет, фото и удаление
                  заблокированы. В казну поступают остатки удалённых счетов, и по умолчанию — оплата за рекламу,
                  продвижение и дизайн карты. При смене прежний счёт вернётся владельцу в исходном виде.
                </p>
                <select
                  value={treasuryAccount}
                  onChange={e => setTreasuryAccount(e.target.value)}
                  disabled={!isAdmin || savingTreasury}
                  style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)'}}
                >
                  <option value="">— Не назначен —</option>
                  {accounts.filter(a => a.account_status === 'ACTIVE').map(a => (
                    <option key={a.account_id} value={a.account_number}>
                      #{a.account_number} ({a.mc_nickname}{a.account_name ? `, ${a.account_name}` : ''})
                    </option>
                  ))}
                </select>
                {isAdmin && (
                  <button className="primary-btn" style={{marginTop: '12px'}} disabled={savingTreasury} onClick={handleSaveTreasury}>
                    {savingTreasury ? 'Сохранение...' : 'Сохранить счёт казны'}
                  </button>
                )}
              </div>

              <div className="card">
                <h3 style={{marginTop: 0}}>Чаты для уведомлений</h3>

                <div>
                  <p style={{fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px'}}>Уведомления банка</p>
                  <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>ID группы, куда бот будет присылать заявки на пополнение и вывод.</p>
                  <input 
                    type="text" 
                    value={notificationChatId} 
                    onChange={e => setNotificationChatId(e.target.value)} 
                    placeholder="Например: -5346282586"
                    disabled={!isAdmin}
                  />
                  {isAdmin && <button className="primary-btn" onClick={handleSaveChatId}>Сохранить ID чата</button>}
                </div>

                <div style={{borderTop: '1px solid var(--border)', margin: '18px 0 15px'}} />

                <div>
                  <p style={{fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px'}}>Заявки склада (маркетплейс)</p>
                  <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>ID группы, куда бот будет присылать заявки на выставление и пополнение товаров на маркетплейсе.</p>
                  <input 
                    type="text" 
                    value={warehouseChatId} 
                    onChange={e => setWarehouseChatId(e.target.value)} 
                    placeholder="Например: -1004428785975"
                    disabled={!isAdmin}
                  />
                  {isAdmin && <button className="primary-btn" onClick={handleSaveWarehouseChatId}>Сохранить ID чата склада</button>}
                </div>

                <div style={{borderTop: '1px solid var(--border)', margin: '18px 0 15px'}} />

                <div>
                  <p style={{fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px'}}>Бэкапы БД</p>
                  <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>ID группы, куда бот присылает файл базы данных: при каждом запуске сервера, ежедневно в 00:00 по МСК и по кнопке ниже.</p>
                  <input
                    type="text"
                    value={backupChatId}
                    onChange={e => setBackupChatId(e.target.value)}
                    placeholder="Например: -1004359133705"
                    disabled={!isAdmin}
                  />
                  {isAdmin && <button className="primary-btn" onClick={handleSaveBackupChatId}>Сохранить ID чата бэкапов</button>}

                  {isAdmin && (
                    <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                      <button className="secondary-btn" disabled={isBackingUp} onClick={handleCreateBackupNow}>
                        {isBackingUp ? 'Отправка...' : 'Сделать бэкап'}
                      </button>
                      <button
                        className="secondary-btn"
                        disabled={isRestoringBackup}
                        onClick={() => backupFileInputRef.current && backupFileInputRef.current.click()}
                      >
                        {isRestoringBackup ? 'Загрузка...' : 'Загрузить'}
                      </button>
                      <input
                        type="file"
                        accept=".db"
                        ref={backupFileInputRef}
                        style={{display: 'none'}}
                        onChange={handleRestoreBackupFile}
                      />
                    </div>
                  )}
                  {isAdmin && <p style={{fontSize: '11px', color: 'var(--text-secondary)', margin: '10px 0 0 0'}}>«Загрузить» заменит текущую базу данных на сервере загруженным файлом .db и перезапустит сервер.</p>}
                </div>
              </div>

              <div className="card">
                <h3 style={{marginTop: 0}}>Глобальная рассылка</h3>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>Отправка сообщения всем зарегистрированным пользователям от лица бота. Можно прикрепить до 10 фото, видео или GIF.</p>

                {/* Панель форматирования — оборачивает выделенный текст тегами Telegram */}
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px'}}>
                  {ANNOUNCE_FORMAT_BTNS.map(btn => (
                    <button
                      key={btn.title}
                      type="button"
                      title={btn.title}
                      disabled={!isAdmin}
                      onClick={() => wrapAnnounceSelection(btn.open, btn.close)}
                      style={{
                        minWidth: '38px', height: '34px', padding: '0 10px', borderRadius: '9px',
                        border: '1.5px solid var(--border)', background: 'var(--surface-alt)',
                        color: 'var(--text-primary)', cursor: isAdmin ? 'pointer' : 'not-allowed',
                        fontSize: '14px', ...btn.style
                      }}
                    >{btn.label}</button>
                  ))}
                  <button
                    type="button"
                    title="Добавить ссылку"
                    disabled={!isAdmin}
                    onClick={() => setLinkModal({ url: '' })}
                    style={{
                      minWidth: '38px', height: '34px', padding: '0 10px', borderRadius: '9px',
                      border: '1.5px solid var(--border)', background: 'var(--surface-alt)',
                      color: 'var(--accent-dark)', cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: '14px'
                    }}
                  >🔗</button>
                </div>

                <textarea
                  ref={announceTextRef}
                  value={announceText}
                  onChange={e => setAnnounceText(e.target.value)}
                  placeholder="Текст объявления... Выделите текст и нажмите кнопку форматирования."
                  style={{height: '90px', resize: 'none'}}
                  disabled={!isAdmin}
                />
                <p style={{fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 10px 0'}}>
                  Разметка вставляется тегами Telegram (можно править вручную). Предпросмотр — как в Telegram: клиент показывает жирный/курсив/спойлер и т.д.
                </p>

                {/* Вложения */}
                {announceMedia.length > 0 && (
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px'}}>
                    {announceMedia.map((m, idx) => (
                      <div key={idx} style={{
                        position: 'relative', width: '64px', height: '64px', borderRadius: '10px',
                        overflow: 'hidden', border: '1.5px solid var(--border)', background: 'var(--surface-alt)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {m.kind === 'video'
                          ? <span style={{fontSize: '26px'}}>🎬</span>
                          : <img src={m.data_base64} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />}
                        {m.kind === 'animation' && (
                          <span style={{position: 'absolute', bottom: '2px', left: '3px', fontSize: '9px', fontWeight: 700, color: '#fff', textShadow: '0 0 3px #000'}}>GIF</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAnnounceMedia(idx)}
                          style={{
                            position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px',
                            borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff',
                            cursor: 'pointer', fontSize: '11px', lineHeight: '18px', padding: 0
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {isAdmin && (
                  <>
                    <input
                      type="file"
                      ref={announceFileRef}
                      accept="image/*,video/*,.gif"
                      multiple
                      onChange={handleAnnounceFiles}
                      style={{display: 'none'}}
                    />
                    <div style={{display: 'flex', gap: '10px'}}>
                      <button
                        type="button"
                        className="secondary-btn"
                        style={{flex: 1}}
                        onClick={() => announceFileRef.current && announceFileRef.current.click()}
                        disabled={announceMedia.length >= 10}
                      >📎 Вложить {announceMedia.length > 0 ? `(${announceMedia.length}/10)` : ''}</button>
                      <button
                        className="primary-btn"
                        style={{flex: 1}}
                        onClick={sendAnnounce}
                        disabled={isAnnouncing}
                      >{isAnnouncing ? 'Отправка...' : 'Отправить всем'}</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}


        </div>
      )}

      {tab === 'bank' && (
        <div key="tab-bank" className="tab-fade">
          <div className="sub-tabs">
            <button className={bankSubTab === 'accounts' ? 'active' : ''} onClick={() => setBankSubTab('accounts')}>Счета</button>
            <button className={bankSubTab === 'withdrawals' ? 'active' : ''} onClick={() => setBankSubTab('withdrawals')}>Вывод</button>
            <button className={bankSubTab === 'deposits' ? 'active' : ''} onClick={() => setBankSubTab('deposits')}>Пополнения</button>
            <button className={bankSubTab === 'invoices' ? 'active' : ''} onClick={() => setBankSubTab('invoices')}>Инвойсы</button>
            <button className={bankSubTab === 'decryptor' ? 'active' : ''} onClick={() => setBankSubTab('decryptor')}>Дешифратор</button>
          </div>
          
          {bankSubTab === 'accounts' && (
            <div key="bank-accounts" className="tab-fade">
              <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                <div className="search-container" style={{flex: 1}}>
                  <input
                    type="text"
                    placeholder="Поиск по номеру или нику..."
                    value={accSearch}
                    onChange={e => setAccSearch(e.target.value)}
                    onFocus={() => setAccSearchFocus(true)}
                    onBlur={() => setTimeout(() => setAccSearchFocus(false), 200)}
                    style={{margin: 0}}
                  />
                  {accSearchFocus && accSearch.trim() && searchedAccounts.length > 0 && (
                    <div className="search-dropdown">
                      {searchedAccounts.slice(0, 6).map(a => (
                        <div
                          key={a.account_id}
                          className="search-item"
                          onClick={() => {
                            setAccSearch(String(a.account_number));
                            setExpandedAccountId(a.account_id);
                            setAccSearchFocus(false);
                          }}
                        >
                          #{a.account_number} — {a.mc_nickname}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <select value={accSort} onChange={e => setAccSort(e.target.value)} style={{flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', margin: 0}}>
                  <option value="number_asc">По номеру (возрастание)</option>
                  <option value="number_desc">По номеру (убывание)</option>
                  <option value="balance_asc">По балансу (возрастание)</option>
                  <option value="balance_desc">По балансу (убывание)</option>
                </select>
              </div>

              {sortedAccounts.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Счета не найдены</p>
              ) : (
                sortedAccounts.map(acc => (
                  <div key={acc.account_id} className="card" onClick={() => setExpandedAccountId(expandedAccountId === acc.account_id ? null : acc.account_id)} style={{cursor: 'pointer', position: 'relative', ...getAccountCardStyle(acc.account_status)}}>
                    
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span style={{fontWeight: 'bold', fontSize: '16px'}}>#{acc.account_number}</span>
                      <span style={{fontWeight: 'bold', color: 'var(--accent-dark)'}}>{acc.balance} сц</span>
                      <span 
                        style={{color: 'var(--accent-dark)', textDecoration: 'underline', fontSize: '14px'}}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTab('general');
                          setSubTab('players');
                          if (!expandedIds.includes(acc.tg_id)) {
                            setExpandedIds(prev => [...prev, acc.tg_id]);
                          }
                        }}
                      >
                        {acc.mc_nickname}
                      </span>
                    </div>
                    {expandedAccountId === acc.account_id && (
                      <div style={{marginTop: '15px', fontSize: '14px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', paddingTop: '10px', lineHeight: '1.6'}}>
                        <p style={{margin: '5px 0'}}><b>Название:</b> {acc.account_name}</p>
                        <p style={{margin: '5px 0'}}><b>Статус:</b> {acc.account_status}</p>
                        <p style={{margin: '5px 0'}}><b>Создан:</b> {formatTime(acc.created_at)}</p>
                        <p style={{margin: '5px 0'}}><b>Последнее использование:</b> {formatTime(acc.last_used)}</p>
                        
                        {acc.account_status === 'BLOCKED' && (
                          <div style={{marginTop: '10px', padding: '10px', background: 'rgba(255,59,48,0.1)', borderRadius: '8px'}}>
                            <p style={{margin: '0 0 5px 0', color: 'var(--danger)', fontSize: '13px'}}><b>Заблокирован:</b> {formatTime(acc.account_blocked_at)}</p>
                            <p style={{margin: '0 0 5px 0', color: 'var(--danger)', fontSize: '13px'}}><b>Причина:</b> {acc.account_block_reason}</p>
                            <p style={{margin: 0, color: 'var(--danger)', fontSize: '13px'}}><b>Администратор:</b> {acc.account_blocked_by}</p>
                          </div>
                        )}
                        {acc.account_status === 'DELETED' && (
                          <div style={{marginTop: '10px', padding: '10px', background: 'rgba(142,142,147,0.1)', borderRadius: '8px'}}>
                            <p style={{margin: '0 0 5px 0', color: 'var(--text-secondary)', fontSize: '13px'}}><b>Удален:</b> {formatTime(acc.account_deleted_at)}</p>
                            <p style={{margin: '0 0 5px 0', color: 'var(--text-secondary)', fontSize: '13px'}}><b>Причина:</b> {acc.account_delete_reason}</p>
                            <p style={{margin: 0, color: 'var(--text-secondary)', fontSize: '13px'}}><b>Администратор:</b> {acc.account_deleted_by}</p>
                          </div>
                        )}

                        <div style={{marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                          <button 
                            className="secondary-btn" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setAdminHistory([]);
                              setAdminHistoryError('');
                              setAdminHistoryPage(1);
                              setAdminFilterType('ALL');
                              setAdminFilterAccount(String(acc.account_id));
                              setAdminHistoryView({ show: true, tgId: acc.tg_id, accountNum: acc.account_number, accountId: acc.account_id });
                            }}
                          >
                            Посмотреть историю
                          </button>
                          {isAdmin && acc.account_status !== 'DELETED' && (
                            <button 
                              className="secondary-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAccountActionClick(acc.tg_id, acc.account_number, acc.account_status === 'BLOCKED' ? 'unblock' : 'block');
                              }}
                            >
                              {acc.account_status === 'BLOCKED' ? 'Разблокировать' : 'Заблокировать'}
                            </button>
                          )}
                          {isAdmin && acc.account_status !== 'DELETED' && (
                            <button 
                              className="secondary-btn" 
                              onClick={(e) => {
                                e.stopPropagation();
                                openAccSettings(acc);
                              }}
                            >
                              Настройки
                            </button>
                          )}
                          {canIssueInvoice && acc.account_status !== 'DELETED' && (
                            <button 
                              className="secondary-btn" 
                              style={{color: 'var(--warning)'}}
                              onClick={(e) => {
                                e.stopPropagation();
                                setInvoiceTarget({ tgId: acc.tg_id });
                                setInvoiceModal(true);
                              }}
                            >
                              Выставить инвойс
                            </button>
                          )}
                          {isAdmin && acc.account_status !== 'DELETED' && (
                            <button 
                              className="secondary-btn" 
                              style={{color: 'var(--danger)'}}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAccountActionClick(acc.tg_id, acc.account_number, 'delete');
                              }}
                            >
                              Удалить
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {(bankSubTab === 'withdrawals' || bankSubTab === 'deposits') && (
            <div key={`bank-${bankSubTab}`} className="tab-fade">
              <AdminBankRequests tgId={tgId} adminNickname={adminNickname} activeSubTab={bankSubTab} canAct={canManageBank}
                focusRequestId={reqFocus && reqFocus.scope === 'bank' ? reqFocus.requestId : null}
                focusNonce={reqFocus && reqFocus.scope === 'bank' ? reqFocus.nonce : 0} />
            </div>
          )}


          {bankSubTab === 'invoices' && (
            <div key="bank-invoices" className="tab-fade">
              {canIssueInvoice && (
              <div className="card" style={{marginBottom: '15px'}}>
                <h4 style={{marginTop: 0}}>Выставить инвойс по нику</h4>
                <div className="search-container">
                  <input 
                    type="text" 
                    placeholder="Никнейм игрока" 
                    value={invoiceNick} 
                    onChange={e => setInvoiceNick(e.target.value)} 
                    onFocus={() => setInvoiceNickFocus(true)}
                    onBlur={() => setTimeout(() => setInvoiceNickFocus(false), 200)}
                  />
                  {invoiceNickFocus && invoiceNick && players.filter(p => p.mc_nickname.toLowerCase().includes(invoiceNick.toLowerCase())).length > 0 && (
                    <div className="search-dropdown">
                      {players.filter(p => p.mc_nickname.toLowerCase().includes(invoiceNick.toLowerCase())).map(p => (
                        <div key={p.tg_id} className="search-item" onClick={() => setInvoiceNick(p.mc_nickname)}>
                          {p.mc_nickname}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{display: 'flex', gap: '10px'}}>
                  <div style={{flex: 1}}>
                    <input type="number" placeholder="Сумма" value={invoiceNickAmount} onChange={e => setInvoiceNickAmount(e.target.value)} />
                  </div>
                  <div className="search-container" style={{flex: 1}}>
                    <input 
                      type="number" 
                      placeholder="Счет зачисления" 
                      value={invoiceNickDest} 
                      onChange={e => setInvoiceNickDest(e.target.value)} 
                      onFocus={() => setInvoiceDestFocus(true)}
                      onBlur={() => setTimeout(() => setInvoiceDestFocus(false), 200)}
                    />
                    {invoiceDestFocus && invoiceNickDest && accounts.filter(a => a.account_status === 'ACTIVE' && String(a.account_number).includes(invoiceNickDest)).length > 0 && (
                      <div className="search-dropdown">
                        {accounts.filter(a => a.account_status === 'ACTIVE' && String(a.account_number).includes(invoiceNickDest)).map(a => (
                          <div key={a.account_number} className="search-item" onClick={() => setInvoiceNickDest(String(a.account_number))}>
                            #{a.account_number} ({a.mc_nickname})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <input type="text" placeholder="Причина" value={invoiceNickReason} onChange={e => setInvoiceNickReason(e.target.value)} />
                <button className="primary-btn" onClick={handleInvoiceByNick}>Выставить</button>
              </div>
              )}
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                <h4 style={{margin: 0}}>Все инвойсы</h4>
              </div>
              {allInvoices.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Инвойсов нет</p>
              ) : (
                allInvoices.map(inv => {
                  const player = players.find(p => String(p.tg_id) === String(inv.tg_id));
                  return (
                  <div key={inv.id} className="card" style={{ borderLeft: inv.status === 'PAID' ? '4px solid var(--success)' : '4px solid var(--danger)' }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                      <span style={{fontWeight: 'bold'}}>Инвойс #{inv.id}</span>
                      <span style={{fontWeight: 'bold', color: inv.status === 'PAID' ? 'var(--success)' : 'var(--danger)'}}>
                        {inv.status === 'PAID' ? 'Оплачен' : 'Не оплачен'}
                      </span>
                    </div>
                    <p style={{margin: '5px 0', fontSize: '14px'}}>
                      <b>Игрок:</b>{' '}
                      <span 
                        style={{color: 'var(--accent-dark)', textDecoration: 'underline', cursor: 'pointer'}}
                        onClick={() => {
                          setTab('general');
                          setSubTab('players');
                          if (!expandedIds.includes(inv.tg_id)) {
                            setExpandedIds(prev => [...prev, inv.tg_id]);
                          }
                        }}
                      >
                        {player ? player.mc_nickname : inv.tg_id}
                      </span>
                    </p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Счет списания:</b> #{inv.account_number}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Счет зачисления:</b> #{inv.destination_account}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Сумма:</b> {inv.amount} сц</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Причина:</b> {inv.reason}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Выдал:</b> {inv.issued_by}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Дата:</b> {formatTime(inv.created_at)}</p>
                    {inv.status === 'PAID' && <p style={{margin: '5px 0', fontSize: '14px'}}><b>Оплачен:</b> {formatTime(inv.paid_at)}</p>}
                  </div>
                );
              })
              )}
            </div>
          )}

          {bankSubTab === 'decryptor' && (
            <div key="bank-decryptor" className="card tab-fade">
              <h3 style={{marginTop: 0}}>Дешифратор квитанций</h3>
              <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Введите код квитанции для расшифровки данных об операции.</p>
              <textarea 
                placeholder="Вставьте код квитанции..."
                value={decryptId || ''}
                onChange={e => setDecryptId(e.target.value)}
                style={{width: '100%', height: '100px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '10px', resize: 'none', boxSizing: 'border-box'}}
              />
              <button className="primary-btn" onClick={handleDecrypt}>Расшифровать</button>
              
              {decryptError && <p style={{color: 'var(--danger)', marginTop: '10px'}}>{decryptError}</p>}
              
              {decryptResult && (
                <div style={{marginTop: '15px', padding: '15px', background: 'var(--bg-subtle)', borderRadius: '8px', fontSize: '14px', lineHeight: '1.6', wordBreak: 'break-word'}}>
                  <p style={{margin: '5px 0'}}><b>Тип:</b> {decryptResult.type}</p>
                  {decryptResult.type === 'Перевод' ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Отправитель (Счет):</b> #{decryptResult.from_account}</p>
                      <p style={{margin: '5px 0'}}><b>Получатель (Счет):</b> #{decryptResult.to_account}</p>
                      {decryptResult.comment && <p style={{margin: '5px 0'}}><b>Комментарий:</b> {decryptResult.comment}</p>}
                    </>
                  ) : decryptResult.type === 'Возврат средств' ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет пополнения:</b> #{decryptResult.account}</p>
                      <p style={{margin: '5px 0'}}><b>Причина:</b> {decryptResult.reason}</p>
                    </>
                  ) : decryptResult.type === 'Пополнение' ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет пополнения:</b> #{decryptResult.account}</p>
                      <p style={{margin: '5px 0'}}><b>Решение:</b> <span style={{fontWeight: 'bold', color: decryptResult.status === 'Одобрено' ? 'var(--success)' : (decryptResult.status === 'Отклонено' ? 'var(--danger)' : 'var(--warning)')}}>{decryptResult.status}</span></p>
                    </>
                  ) : (decryptResult.type === 'Оплата рекламы' || decryptResult.type === 'Оплата инвойса' || decryptResult.type === 'Дизайн карты') ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет списания:</b> #{decryptResult.account}</p>
                      <p style={{margin: '5px 0'}}><b>Куда:</b> {decryptResult.to_account ? `Счет #${decryptResult.to_account}` : 'Нет данных (старая квитанция)'}</p>
                    </>
                  ) : decryptResult.type === 'Вывод средств' ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет списания:</b> #{decryptResult.account}</p>
                      <p style={{margin: '5px 0'}}><b>Куда:</b> {decryptResult.dest} ({decryptResult.dest_val})</p>
                      <p style={{margin: '5px 0'}}><b>Решение:</b> <span style={{fontWeight: 'bold', color: decryptResult.status === 'Одобрено' ? 'var(--success)' : (decryptResult.status === 'Отклонено' ? 'var(--danger)' : 'var(--warning)')}}>{decryptResult.status}</span></p>
                      {decryptResult.status === 'Отклонено' && decryptResult.reason && <p style={{margin: '5px 0'}}><b>Причина:</b> {decryptResult.reason}</p>}
                    </>
                  ) : decryptResult.type === 'Заказ на маркетплейсе' ? (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет списания:</b> #{decryptResult.account}</p>
                      {decryptResult.items && decryptResult.items.length > 0 && (
                        <div style={{margin: '5px 0'}}>
                          <b>Состав заказа:</b>
                          <ul style={{margin: '5px 0 0 0', paddingLeft: '20px'}}>
                            {decryptResult.items.map((it, idx) => (
                              <li key={idx}>
                                {it.name || 'Неизвестный товар'}
                                {it.qty ? ` ×${it.qty}` : ''}
                                {' — ячейка: '}
                                {it.cell ? (
                                  it.item_id ? (
                                    <span
                                      style={{color: 'var(--accent-dark)', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold'}}
                                      onClick={() => jumpToItemInAdmin(it.item_id)}
                                    >
                                      {it.cell}
                                    </span>
                                  ) : it.cell
                                ) : '—'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p style={{margin: '5px 0'}}><b>Статус:</b> <span style={{fontWeight: 'bold', color: decryptResult.status === 'Доставлен' ? 'var(--success)' : (decryptResult.status === 'Отменён' ? 'var(--danger)' : 'var(--warning)')}}>{decryptResult.status}</span></p>
                    </>
                  ) : (
                    <>
                      <p style={{margin: '5px 0'}}><b>Счет:</b> #{decryptResult.account}</p>
                      {decryptResult.cell && <p style={{margin: '5px 0'}}><b>Ячейка склада:</b> №{decryptResult.cell}</p>}
                    </>
                  )}
                  <p style={{margin: '5px 0'}}><b>Сумма:</b> {decryptResult.amount} сц</p>
                  <p style={{margin: '5px 0'}}><b>Дата:</b> {new Date(decryptResult.date).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'market' && (
        <div key="tab-market" className="tab-fade">
          <div className="sub-tabs">
            <button className={marketSubTab === 'overview' ? 'active' : ''} onClick={() => setMarketSubTab('overview')}>Общее</button>
            <button className={marketSubTab === 'requests' ? 'active' : ''} onClick={() => setMarketSubTab('requests')}>Заявки на склад</button>
            <button className={marketSubTab === 'orders' ? 'active' : ''} onClick={() => setMarketSubTab('orders')}>Заказы</button>
            <button className={marketSubTab === 'ads' ? 'active' : ''} onClick={() => setMarketSubTab('ads')}>Реклама</button>
          </div>

          {marketSubTab === 'overview' && (
            <div key="market-overview" className="tab-fade">
              {marketItems.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Товаров пока нет</p>
              ) : (
                marketItems.map(item => (
                  <div key={item.id} className="card">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <div style={{width: '36px', height: '36px', background: 'var(--accent-soft)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                          {item.image_url
                            ? <img src={item.image_url} alt="" style={{width: '26px', height: '26px', imageRendering: 'pixelated'}} />
                            : <span style={{fontSize: '18px'}}>📦</span>}
                        </div>
                        <h4 style={{margin: 0}}>{item.name}</h4>
                      </div>
                      <span style={{fontSize: '12px', fontWeight: 'bold', color: item.status === 'ACTIVE' ? 'var(--success)' : 'var(--text-secondary)'}}>
                        {item.status === 'ACTIVE' ? 'Активен' : 'Скрыт'}
                      </span>
                    </div>
                    <p style={{margin: '4px 0', fontSize: '14px'}}>
                      <b>Продавец:</b>{' '}
                      <span
                        style={{color: 'var(--accent-dark)', textDecoration: 'underline', cursor: 'pointer'}}
                        onClick={() => {
                          setTab('general');
                          setSubTab('players');
                          if (!expandedIds.includes(item.tg_id)) {
                            setExpandedIds(prev => [...prev, item.tg_id]);
                          }
                        }}
                      >
                        {item.mc_nickname || item.tg_id}
                      </span>
                    </p>
                    <p style={{margin: '4px 0', fontSize: '14px'}}><b>Категория:</b> {item.category || '—'}</p>
                    {item.cell_number && (
                      <p style={{margin: '4px 0', fontSize: '15px', background: 'var(--accent-soft)', display: 'inline-block', padding: '3px 10px', borderRadius: '8px'}}>
                        📦 <b>Ячейка №{item.cell_number}</b>
                      </p>
                    )}
                    <p style={{margin: '4px 0', fontSize: '14px'}}><b>Цена за лот:</b> {item.price} сц ({item.unit_qty} шт./лот)</p>
                    <p style={{margin: '4px 0', fontSize: '14px'}}><b>Остаток:</b> {item.stock_qty} шт.</p>
                    <p style={{margin: '4px 0', fontSize: '14px'}}><b>Продано лотов:</b> {item.purchases_count || 0}</p>
                    {item.promo_expires_at && new Date(item.promo_expires_at + 'Z').getTime() > Date.now() && (
                      <p style={{margin: '4px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <span style={{width: '10px', height: '10px', borderRadius: '50%', background: item.promo_color, flexShrink: 0}} />
                        <b>Продвигается</b> {item.promo_label ? `(«${item.promo_label}») ` : ''}до {new Date(item.promo_expires_at + 'Z').toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', dateStyle: 'short', timeStyle: 'short'})}
                      </p>
                    )}
                    <p style={{margin: '4px 0', fontSize: '14px'}}>
                      <b>Счёт для оплаты:</b> {item.payout_account ? `#${item.payout_account}` : 'Не найден'}
                      {item.seller_account_status && item.seller_account_status !== 'ACTIVE' && (
                        <span style={{color: 'var(--danger)'}}> ({item.seller_account_status === 'BLOCKED' ? 'заблокирован' : 'удален'})</span>
                      )}
                    </p>
                    {item.description && <p style={{margin: '4px 0', fontSize: '13px', color: 'var(--text-secondary)'}}>{item.description}</p>}

                    <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                      {isAdmin && <button className="secondary-btn" onClick={() => setEditItemModal({ ...item })}>Изменить</button>}
                      {(isAdmin || canManageWarehouse) && <button className="secondary-btn" style={{color: 'var(--danger)'}} onClick={() => { setDeleteItemModal(item); setDeleteItemReason(''); setDeleteItemError(''); }}>🗑 Удалить</button>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {(marketSubTab === 'requests' || marketSubTab === 'orders') && (
            <div key={`market-${marketSubTab}`} className="tab-fade">
              <AdminMarketRequests tgId={tgId} adminNickname={adminNickname} activeSubTab={marketSubTab} canAct={canManageWarehouse}
                focusRequestId={reqFocus && reqFocus.scope === 'market' ? reqFocus.requestId : null}
                focusNonce={reqFocus && reqFocus.scope === 'market' ? reqFocus.nonce : 0} />
            </div>
          )}

          {marketSubTab === 'ads' && (
            <div key="market-ads" className="tab-fade">
              <div className="card">
                <h3 style={{marginTop: 0}}>Статистика</h3>
                <p>Активных баннеров: <b>{adStats.active_ads}</b></p>
                <p>Карточек товаров под продвижением: <b>{adStats.active_promotions}</b></p>
                <p>Доход за сегодня: <b style={{color: 'var(--success)'}}>{adStats.revenue_today} сц</b></p>
              </div>

              <div className="card">
                <h3 style={{marginTop: 0}}>Настройка рекламы</h3>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px'}}>Укажите стоимость размещения одного баннера за 1 сутки (в сц).</p>
                <input 
                  type="number" 
                  value={adPrice} 
                  onChange={e => setAdPrice(e.target.value)} 
                  placeholder="Цена в сц"
                  disabled={!isAdmin}
                />
                <label style={{fontSize: '12px', fontWeight: 'bold', display: 'block', marginTop: '10px'}}>Счет для зачисления оплаты за рекламу и продвижение</label>
                <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px'}}>Куда будет поступать оплата за размещение рекламы и за продвижение товаров.</p>
                <select
                  value={adCardNumber}
                  onChange={e => setAdCardNumber(e.target.value)}
                  disabled={!isAdmin}
                  style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)'}}
                >
                  <option value="">— По умолчанию (счёт казны) —</option>
                  {accounts.filter(a => a.account_status === 'ACTIVE').map(a => (
                    <option key={a.account_number} value={a.account_number}>
                      #{a.account_number} ({a.mc_nickname}{a.account_name ? `, ${a.account_name}` : ''})
                    </option>
                  ))}
                </select>

                <h3 style={{marginTop: '20px'}}>Настройка продвижения товаров</h3>
                <label style={{fontSize: '12px', fontWeight: 'bold', display: 'block'}}>Стоимость продвижения за 1 день (сц)</label>
                <input
                  type="number"
                  value={promotionPricePerDay}
                  onChange={e => setPromotionPricePerDay(e.target.value)}
                  placeholder="Цена в сц"
                  disabled={!isAdmin}
                />
                <label style={{fontSize: '12px', fontWeight: 'bold', display: 'block', marginTop: '10px'}}>Стоимость ярлыка (доплата, сц/день)</label>
                <input
                  type="number"
                  value={promotionLabelPrice}
                  onChange={e => setPromotionLabelPrice(e.target.value)}
                  placeholder="Цена в сц"
                  disabled={!isAdmin}
                />

                <label style={{fontSize: '12px', fontWeight: 'bold', display: 'block', marginTop: '10px'}}>Стоимость уникального дизайна банковской карты (сц)</label>
                <input
                  type="number"
                  value={cardDesignPrice}
                  onChange={e => setCardDesignPrice(e.target.value)}
                  placeholder="Цена в сц"
                  disabled={!isAdmin}
                />
                <p style={{fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 0 0'}}>
                  Средства идут на счёт, выбранный выше для оплаты рекламы и продвижения.
                </p>

                {isAdmin && <button className="primary-btn" style={{marginTop: '15px'}} onClick={handleSaveAdAndPromotionSettings}>Сохранить</button>}
              </div>

              {adminAds.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Нет баннеров</p>
              ) : (
                adminAds.map(ad => (
                  <div key={ad.id} className="card">
                    <div className="ad-banner-card" style={{backgroundImage: `url(${ad.image_url})`, backgroundSize: ad.bg_size || 'cover', backgroundPosition: ad.bg_position, marginBottom: '15px'}}>
                      <div className="ad-overlay"></div>
                      <div className="ad-badge">Реклама</div>
                      <h4 style={{position: 'relative', zIndex: 2}}>{ad.title}</h4>
                      <p style={{position: 'relative', zIndex: 2}}>{ad.description}</p>
                      <div className="ad-bottom" style={{position: 'relative', zIndex: 2}}>
                        <button className="ad-btn">{ad.button_text}</button>
                      </div>
                      <div style={{position: 'relative', zIndex: 2, textAlign: 'right', marginTop: '4px'}}>
                        <span className="ad-author">от {ad.author_nickname || 'Игрока'}</span>
                      </div>
                    </div>
                    <p style={{margin: '5px 0', fontSize: '14px'}}>
                      <b>Рекламодатель:</b>{' '}
                      <span
                        style={{color: 'var(--accent-dark)', textDecoration: 'underline', cursor: 'pointer'}}
                        onClick={() => {
                          setTab('general');
                          setSubTab('players');
                          if (!expandedIds.includes(ad.tg_id)) {
                            setExpandedIds(prev => [...prev, ad.tg_id]);
                          }
                        }}
                      >
                        {ad.author_nickname || ad.tg_id}
                      </span>
                    </p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Оплачено:</b> {ad.total_price} сц</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Даты:</b> {ad.start_date} — {ad.end_date}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Ссылка:</b> {ad.link_url || 'Нет'}</p>
                    <p style={{margin: '5px 0', fontSize: '14px'}}><b>Статус:</b> {ad.status === 'ACTIVE' ? '✅ Активен' : '⏸ Неактивен'}</p>

                    {isAdmin && (
                      <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                        <button className="secondary-btn" onClick={() => setEditAdModal(ad)}>Изменить</button>
                        <button className="secondary-btn" style={{color: 'var(--danger)'}} onClick={() => setDeleteAdModal(ad)}>Удалить</button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Модалка фильтров игроков */}
      {filterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Фильтры игроков</h3>
            
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Наличие счета</label>
            <select 
              value={tempFilters.account} 
              onChange={e => setTempFilters({...tempFilters, account: e.target.value})} 
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            >
              <option value="ALL">Все</option>
              <option value="HAS_ACCOUNT">С банковским счетом</option>
              <option value="NO_ACCOUNT">Без счета</option>
            </select>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Баланс</label>
            <select 
              value={tempFilters.balance} 
              onChange={e => setTempFilters({...tempFilters, balance: e.target.value})} 
              style={{width: '100%', padding: '10px', marginBottom: '15px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            >
              <option value="ALL">Любой</option>
              <option value="POSITIVE">Больше нуля</option>
            </select>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Статус счета</label>
            <select 
              value={tempFilters.status} 
              onChange={e => setTempFilters({...tempFilters, status: e.target.value})} 
              style={{width: '100%', padding: '10px', marginBottom: '20px', marginTop: '5px', borderRadius: '8px', border: '1px solid var(--border)'}}
            >
              <option value="ALL">Любой</option>
              <option value="ACTIVE">Активный</option>
              <option value="BLOCKED">Заблокирован</option>
              <option value="DELETED">Удален</option>
            </select>

            <div className="modal-actions">
              <button className="primary-btn" onClick={applyFilters}>Применить</button>
              <button className="secondary-btn" onClick={resetFilters}>Сбросить</button>
            </div>
            <button className="link-btn" style={{width: '100%', marginTop: '15px'}} onClick={() => setFilterModal(false)}>Закрыть</button>
          </div>
        </div>
      )}

      {/* Модалка бана */}
      {banModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Блокировка игрока</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Укажите причину блокировки. Она будет показана игроку.</p>
            <input 
              type="text" 
              placeholder="Причина бана..."
              value={banReason || ''}
              onChange={e => setBanReason(e.target.value)}
            />
            {banError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{banError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleAction(banTarget, 'ban', banReason)}>Забанить</button>
              <button className="secondary-btn" onClick={() => { setBanModal(false); setBanReason(''); setBanTarget(null); setBanError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка выставления инвойса */}
      {invoiceModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Выставить инвойс</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Игрок не сможет переводить и выводить средства до оплаты инвойса.</p>
            <input 
              type="number" 
              placeholder="Сумма инвойса..."
              value={invoiceAmount || ''}
              onChange={e => setInvoiceAmount(e.target.value)}
            />
            <div className="search-container">
              <input 
                type="number" 
                placeholder="Счет зачисления (куда уйдут деньги)..."
                value={invoiceDestAccount || ''}
                onChange={e => setInvoiceDestAccount(e.target.value)}
                onFocus={() => setInvoiceModalDestFocus(true)}
                onBlur={() => setTimeout(() => setInvoiceModalDestFocus(false), 200)}
              />
              {invoiceModalDestFocus && invoiceDestAccount && accounts.filter(a => a.account_status === 'ACTIVE' && String(a.account_number).includes(invoiceDestAccount)).length > 0 && (
                <div className="search-dropdown">
                  {accounts.filter(a => a.account_status === 'ACTIVE' && String(a.account_number).includes(invoiceDestAccount)).map(a => (
                    <div key={a.account_number} className="search-item" onClick={() => setInvoiceDestAccount(String(a.account_number))}>
                      #{a.account_number} ({a.mc_nickname})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input 
              type="text" 
              placeholder="Причина инвойса..."
              value={invoiceReason || ''}
              onChange={e => setInvoiceReason(e.target.value)}
            />
            {invoiceError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{invoiceError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--warning)'}} onClick={handleInvoice}>Выставить</button>
              <button className="secondary-btn" onClick={() => { setInvoiceModal(false); setInvoiceAmount(''); setInvoiceReason(''); setInvoiceDestAccount(''); setInvoiceTarget(null); setInvoiceError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка действий со счетом (Блок/Удаление) */}
      {accActionModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>
              {accActionModal.action === 'block' ? 'Блокировка счета' : 'Удаление счета'} #{accActionModal.accountNum}
            </h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Укажите причину. Она будет отправлена владельцу.</p>
            <input 
              type="text" 
              placeholder="Причина..."
              value={accActionReason || ''}
              onChange={e => setAccActionReason(e.target.value)}
            />
            {accActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{accActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => executeAccountAction(accActionModal.tgId, accActionModal.accountNum, accActionModal.action, accActionReason)}>
                {accActionModal.action === 'block' ? 'Заблокировать' : 'Удалить'}
              </button>
              <button className="secondary-btn" onClick={() => { setAccActionModal({show: false, action: '', tgId: null, accountNum: null}); setAccActionReason(''); setAccActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка настроек счета (Админ) */}
      {accSettingsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Настройки счета</h3>

            <div
              className={`account-card ${accEditData.image ? 'has-image' : ''}`}
              style={{
                width: '180px',
                margin: '0 auto 15px auto',
                ...(accEditData.image ? { backgroundImage: `url(${accEditData.image})` } : { background: accEditData.color })
              }}
            >
              <div className="account-card-inner">
                <h3>{accEditData.name} #{accEditData.number}</h3>
                <p className="account-balance" style={{fontSize: '18px'}}>{accEditData.balance} сц</p>
              </div>
            </div>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Номер счета</label>
            <input 
              type="number" 
              value={accEditData.number || ''} 
              onChange={e => setAccEditData({...accEditData, number: e.target.value})} 
            />
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Название</label>
            <input 
              type="text" 
              value={accEditData.name || ''} 
              onChange={e => setAccEditData({...accEditData, name: e.target.value})} 
            />
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Баланс</label>
            <input 
              type="number" 
              value={accEditData.balance || ''} 
              onChange={e => setAccEditData({...accEditData, balance: e.target.value})} 
            />
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Цвет</label>
            <div className="color-picker">
              {COLORS.map(c => (
                <div 
                  key={c} 
                  className={`color-circle ${accEditData.color === c ? 'selected' : ''}`} 
                  style={{background: c}} 
                  onClick={() => setAccEditData({...accEditData, color: c})} 
                />
              ))}
            </div>

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Дизайн карты (фото)</label>
            <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', marginBottom: '8px'}}>
              Изменение и удаление фото администратором — бесплатно.
            </p>
            <input type="file" accept="image/*" onChange={handleAccDesignFileChange} style={{fontSize: '14px', padding: '8px'}} />
            {accEditData.image && (
              <button className="secondary-btn" style={{marginTop: '10px'}} onClick={handleAccDesignRemove}>Убрать фото</button>
            )}

            <div className="modal-actions">
              <button className="primary-btn" onClick={saveAccSettings}>Сохранить</button>
              <button className="secondary-btn" onClick={() => setAccSettingsModal(false)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {editItemModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Редактирование товара</h3>
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Название</label>
            <input type="text" value={editItemModal.name} onChange={e => setEditItemModal({...editItemModal, name: e.target.value})} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Категория</label>
            <input type="text" value={editItemModal.category} onChange={e => setEditItemModal({...editItemModal, category: e.target.value})} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Описание</label>
            <textarea value={editItemModal.description} onChange={e => setEditItemModal({...editItemModal, description: e.target.value})} style={{height: '60px', resize: 'none'}} />
            <div style={{display: 'flex', gap: '10px'}}>
              <div style={{flex: 1}}>
                <label style={{fontSize: '12px', fontWeight: 'bold'}}>Кол-во в лоте</label>
                <input type="number" value={editItemModal.unit_qty} onChange={e => setEditItemModal({...editItemModal, unit_qty: e.target.value})} />
              </div>
              <div style={{flex: 1}}>
                <label style={{fontSize: '12px', fontWeight: 'bold'}}>Цена за лот</label>
                <input type="number" value={editItemModal.price} onChange={e => setEditItemModal({...editItemModal, price: e.target.value})} />
              </div>
            </div>
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Остаток (шт.)</label>
            <input type="number" value={editItemModal.stock_qty} onChange={e => setEditItemModal({...editItemModal, stock_qty: e.target.value})} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Статус</label>
            <select value={editItemModal.status} onChange={e => setEditItemModal({...editItemModal, status: e.target.value})} style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '15px'}}>
              <option value="ACTIVE">Активен (виден в маркете)</option>
              <option value="HIDDEN">Скрыт</option>
            </select>
            <div className="modal-actions">
              <button className="primary-btn" onClick={handleUpdateMarketItem}>Сохранить</button>
              <button className="secondary-btn" onClick={() => setEditItemModal(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {deleteItemModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Удаление товара</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>
              Товар «{deleteItemModal.name}» будет снят с продажи, ячейка склада полностью освободится. Укажите причину — она будет отправлена продавцу.
            </p>
            <input
              type="text"
              placeholder="Причина удаления..."
              value={deleteItemReason}
              onChange={e => setDeleteItemReason(e.target.value)}
            />
            {deleteItemError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{deleteItemError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} disabled={isDeletingItem} onClick={handleDeleteMarketItem}>{isDeletingItem ? 'Удаление...' : 'Удалить'}</button>
              <button className="secondary-btn" disabled={isDeletingItem} onClick={() => { setDeleteItemModal(null); setDeleteItemReason(''); setDeleteItemError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {editAdModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Настройка баннера</h3>
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Заголовок</label>
            <input type="text" value={editAdModal.title} onChange={e => setEditAdModal({...editAdModal, title: e.target.value})} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Описание</label>
            <textarea value={editAdModal.description} onChange={e => setEditAdModal({...editAdModal, description: e.target.value})} style={{height: '60px', resize: 'none'}} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Текст кнопки</label>
            <input type="text" value={editAdModal.button_text} onChange={e => setEditAdModal({...editAdModal, button_text: e.target.value})} />
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Ссылка</label>
            <input type="text" value={editAdModal.link_url} onChange={e => setEditAdModal({...editAdModal, link_url: e.target.value})} />
            <div style={{display: 'flex', gap: '10px'}}>
              <div style={{flex: 1}}>
                <label style={{fontSize: '12px', fontWeight: 'bold'}}>Начало</label>
                <input type="date" value={editAdModal.start_date} onChange={e => setEditAdModal({...editAdModal, start_date: e.target.value})} />
              </div>
              <div style={{flex: 1}}>
                <label style={{fontSize: '12px', fontWeight: 'bold'}}>Конец</label>
                <input type="date" value={editAdModal.end_date} onChange={e => setEditAdModal({...editAdModal, end_date: e.target.value})} />
              </div>
            </div>
            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Статус</label>
            <select value={editAdModal.status} onChange={e => setEditAdModal({...editAdModal, status: e.target.value})} style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '15px'}}>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
              <option value="EXPIRED">EXPIRED</option>
            </select>
            <div className="modal-actions">
              <button className="primary-btn" onClick={handleUpdateAd}>Сохранить</button>
              <button className="secondary-btn" onClick={() => setEditAdModal(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {deleteAdModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Удаление баннера</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>
              Удалить рекламный баннер «{deleteAdModal.title}»? Это действие необратимо, деньги за рекламу не возвращаются автоматически.
            </p>
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleDeleteAd(deleteAdModal.id)}>Удалить</button>
              <button className="secondary-btn" onClick={() => setDeleteAdModal(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {linkModal && (
        <div className="modal-overlay" onClick={() => setLinkModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{marginTop: 0}}>Вставить ссылку</h3>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '10px'}}>
              Выделенный в тексте фрагмент станет ссылкой. Если ничего не выделено — тег вставится в позицию курсора.
            </p>
            <input
              type="url"
              autoFocus
              value={linkModal.url}
              onChange={e => setLinkModal({ url: e.target.value })}
              placeholder="https://..."
              style={{width: '100%', padding: '11px', borderRadius: '10px', border: '1.5px solid var(--border)', marginBottom: '15px', boxSizing: 'border-box'}}
              onKeyDown={e => { if (e.key === 'Enter') applyAnnounceLink(); }}
            />
            <div className="modal-actions">
              <button className="primary-btn" onClick={applyAnnounceLink}>Вставить</button>
              <button className="secondary-btn" onClick={() => setLinkModal(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;